import { API_ENDPOINTS, AppConfigService } from './app-config-service';
import { COOKIE_KEYS } from '../models/client';

export const ANONYMOUS_USER_NAME: string = 'anonymous';

type NewCookieKey = 'usid' | 'scid' | 'scShipType';

const NEW_COOKIE_KEYS: {
  US_ID: NewCookieKey;
  SC_ID: NewCookieKey;
  SC_SHIP_TYPE: NewCookieKey
} = {
  US_ID: 'usid',
  SC_ID: 'scid',
  SC_SHIP_TYPE: 'scShipType',
};

type GetCookieFn = (cname: string) => string;

export type UserType = 'b2c' | 'b2b';

export type DisplayType = 'b2ccmgt' | 'b2bcmgt' | 'cmgtsc' | 'cmgtpscms';

export type UserDetails<T = number> = {
  userId: T;
  userName: string;
  countryCode: string;
  langCode: string;
  userType: UserType;
  customerType: DisplayType;
  isB2BMasterUser: boolean;
  isGuestUser?: boolean;
  isCSR?: boolean;
  sessionToken?: string;
  cartSessionId?: string;
  scId?: string;
  scShipType?: string;
  effectiveUserDisplayType?: DisplayType;
};

type UserCookieInfo = {
  firstName: string;
  lastName: string;
  shipToAccountNumber: number;
} & UserDetails<string>;

export type UserData = {
  userId?: number;
  userName?: string;
  countryCode?: string;
  displayType?: DisplayType;
  userType: UserType;
  effectiveUserDisplayType?: DisplayType;
};

export type UserContext = {
  isAnonymous: boolean;
  isB2BMasterUser: boolean;
  effectiveUserId?: number;
  effectiveUserName?: string;
  guestUserId?: number;
  guestUserName?: string;
  lang?: string;
  country?: string;
  scId?: string;
  scShipType?: string;
  sessionId?: string;
  userAgent?: string;
  effectiveUser?: UserData;
} & UserData;

export type UserTypeResponse = {
  apiStatus: string;
  elapseTimeMillis: number;
  message: string;
  data: UserContext;
};

export interface UserDetailsService {
  getUserDetails(): ng.IPromise<UserDetails>;
}

const ANONYMOUS_USER: UserDetails = {
  userId: 0,
  userName: ANONYMOUS_USER_NAME,
  countryCode: 'us',
  langCode: 'en',
  customerType: 'b2ccmgt',
  userType: 'b2c',
  isB2BMasterUser: false,
};

export class CookieUserDetailsImpl implements UserDetailsService {

  constructor(private $q: ng.IQService,
              private configService: AppConfigService) {
  }

  public getUserDetails(): ng.IPromise<UserDetails> {
    const userCookieInfo: UserCookieInfo = this.getCommerceUser(this.configService.getCookieFor);
    const { userName, countryCode, customerType, langCode, userId: userIdString, sessionToken, cartSessionId } = userCookieInfo;
    let user: UserDetails;
    if (userName === ANONYMOUS_USER_NAME) {
      user = {
        ...ANONYMOUS_USER,
        ...{
          countryCode,
          customerType,
          langCode,
          sessionToken,
          cartSessionId,
        },
      };
    } else {
      const userId = parseInt(userIdString, 10);
      user = {
        ...ANONYMOUS_USER,
        ... {
          countryCode,
          customerType,
          langCode,
          userName,
          sessionToken,
          cartSessionId,
          userId,
        },
      };
    }
    return this.$q.resolve(user);
  }

  private getCommerceUser(getCookie: GetCookieFn): UserCookieInfo {
    const LOGIN_KEY = 'userlogin';
    const USERID_KEY = 'userkey';
    const F_NAME_KEY = 'customerName';
    const L_NAME_KEY = 'lastName';
    const CUSTOMER_TYPE_KEY = 'displayType';
    const SHIP_TO_ACCOUNT_KEY = 'shipToNumber';
    const ISB2B_KEY = 'isB2BCMGT';
    const ISB2B_MASTER_KEY = 'MasterUserType';
    const EFFECTIVE_USER_ID_KEY = 'effectiveUserKey';
    const userInfo: UserCookieInfo = {
      firstName: '',
      lastName: '',
      userId: '',
      userName: '',
      userType: 'b2c',
      isB2BMasterUser: false,
      customerType: getCookie(COOKIE_KEYS.CK_DISPLAY_TYPE) as DisplayType,
      shipToAccountNumber: 0,
      countryCode: getCookie(COOKIE_KEYS.CK_ISO_CODE),
      langCode: getCookie(COOKIE_KEYS.CK_LANG_CODE),
      sessionToken: getCookie(COOKIE_KEYS.LT_TOKEN_ID),
      cartSessionId: getCookie(COOKIE_KEYS.CART_SESSION_ID),
      scId: getCookie(NEW_COOKIE_KEYS.SC_ID),
      scShipType: getCookie(NEW_COOKIE_KEYS.SC_SHIP_TYPE),
    };
    try {
      const customerInfo = getCookie(COOKIE_KEYS.CK_CUSTOMER_INFO);
      if (!customerInfo || customerInfo.length <= 0) {
        userInfo.userName =  ANONYMOUS_USER_NAME;
        return userInfo;
      }
      const userInfoString = decodeURIComponent(customerInfo);
      userInfoString.split(';').forEach((line) => {
        let key = '';
        let value = '';
        const entries = line.split(':');
        if (entries.length > 0) {
          key = entries[0];
        }
        if (entries.length > 1) {
          value = entries[1];
        }
        switch (key) {
          case F_NAME_KEY:
            userInfo.firstName = value;
            break;
          case L_NAME_KEY:
            userInfo.lastName = value;
            break;
          case USERID_KEY:
            userInfo.userId = value;
            break;
          case LOGIN_KEY:
            userInfo.userName = value;
            break;
          case CUSTOMER_TYPE_KEY:
            userInfo.customerType = value as DisplayType;
            break;
          case ISB2B_KEY:
            userInfo.userType = value === 'true' ? 'b2b' : 'b2c';
            break;
          case ISB2B_MASTER_KEY:
            userInfo.isB2BMasterUser = value !== 'NONE';
            break;
          case SHIP_TO_ACCOUNT_KEY:
            userInfo.shipToAccountNumber = parseInt(value, 10);
            break;
          case EFFECTIVE_USER_ID_KEY:
            userInfo.isCSR = !!(parseInt(value, 10) || 0);
            break;
          default:
            break;
        }
      });
    } catch (e) {
      console.error(e);
    }
    return userInfo;
  }
}

export class UserTypeApiImpl implements UserDetailsService {

  constructor(private $q: ng.IQService,
              private $http: ng.IHttpService,
              private configService: AppConfigService) {
  }

  public getUserDetails(): ng.IPromise<UserDetails> {
    const path: string = `${this.configService.pathFor(API_ENDPOINTS.GET_USER_DETAILS)}`;
    const deferred: ng.IDeferred<UserDetails> = this.$q.defer();
    this.$http.get<UserTypeResponse>(path, { responseType: 'json' })
      .then((response: ng.IHttpResponse<UserTypeResponse>) => {
        if (response.status === 200) {
          const { data: responseData } = response;
          const { data: userInfo } = responseData;
          const {
            userId: loggedInUserId,
            userName: loggedInUserName,
            guestUserId,
            country,
            countryCode: userCountryCode,
            lang,
            displayType,
            isB2BMasterUser,
            userType,
            scId,
            scShipType,
            effectiveUser,
          } = userInfo;
          let { effectiveUserId, effectiveUserName } = userInfo;
          if (effectiveUser) {
            effectiveUserId = effectiveUser.userId;
            effectiveUserName = effectiveUser.userName;
          }
          const userId = effectiveUserId || loggedInUserId || 0;
          const userName = effectiveUserName || loggedInUserName || ANONYMOUS_USER_NAME;
          const countryCode = (effectiveUser ? effectiveUser.countryCode : undefined) || (country || userCountryCode) as string;
          const langCode = lang as string;
          const customerType = displayType as DisplayType;
          const cartSessionId = '';
          const sessionToken = '';
          const isGuestUser = typeof guestUserId === 'number';
          const isCSR = !!effectiveUserId;
          // Code for adding  logic for sending effective user display type as well to backend
          const effectiveUserDisplayType = effectiveUser ? effectiveUser.displayType : undefined;
          const user: UserDetails = {
            userId,
            userName,
            cartSessionId,
            sessionToken,
            countryCode,
            langCode,
            customerType,
            userType,
            isB2BMasterUser,
            isGuestUser,
            isCSR,
            scId,
            scShipType,
            effectiveUserDisplayType,
          };
          deferred.resolve(user);
        } else {
          deferred.resolve({
            ...ANONYMOUS_USER,
            countryCode: undefined as any,
            langCode: undefined as any,
            customerType: undefined as any,
          });
        }
      }, () => {
        deferred.resolve({
          ...ANONYMOUS_USER,
            countryCode: undefined as any,
            langCode: undefined as any,
            customerType: undefined as any,
        });
      });
    return deferred.promise;
  }
}

export class UserTypePrimaryCookieSecondaryImpl implements UserDetailsService {

  private userApiService: UserDetailsService;
  private userCookieService: UserDetailsService;

  constructor(private $q: ng.IQService,
              $http: ng.IHttpService,
              configService: AppConfigService) {
    this.userApiService = new UserTypeApiImpl($q, $http, configService);
    this.userCookieService = new CookieUserDetailsImpl($q, configService);
  }

  public getUserDetails(): ng.IPromise<UserDetails> {
    const deferred: ng.IDeferred<UserDetails> = this.$q.defer();
    this.$q.all([
      this.userApiService.getUserDetails(),
      this.userCookieService.getUserDetails(),
    ]).then((userData) => {
      const [apiUser, cookieUser] = userData;
      const user = {
        ...apiUser,
      };
      // for any property not defined, get the
      // value from the cookie based as fallback
      Object.keys(user).forEach((key) => {
        user[key] = user[key] || cookieUser[key];
      });
      if (user.countryCode) {
        user.countryCode = user.countryCode.toLowerCase();
      }
      if (user.langCode) {
        user.langCode = user.langCode.toLowerCase();
      }
      // legacy behavior previously had cookies
      // tied to sessions. if they no longer exist,
      // fallback to userId instead
      if (!user.sessionToken) {
        user.sessionToken = `${user.userId}`;
      }
      if (!user.cartSessionId) {
        user.cartSessionId = `${user.userId}`;
      }
      deferred.resolve(user);
    }, deferred.reject);
    return deferred.promise;
  }
}

export const USER_DETAILS__SERVICE_NAME = 'userDetailsService';
export const UserDetailsServiceFactory = ($q: ng.IQService,
                                          $http: ng.IHttpService,
                                          configService: AppConfigService): UserDetailsService => {
  return new UserTypePrimaryCookieSecondaryImpl($q, $http, configService);
};

export const asUserContext = (userDetails: UserDetails): UserContext => {
  const {
    userName,
    userId,
    langCode: lang,
    countryCode: country,
    customerType,
    isB2BMasterUser,
    userType,
    scId,
    scShipType,
    effectiveUserDisplayType,
  } =  userDetails;
  const displayType = customerType as DisplayType;
  const isAnonymous = userName === ANONYMOUS_USER_NAME;
  const userContext: UserContext = {
    isB2BMasterUser,
    isAnonymous,
    country,
    lang,
    displayType,
    userType,
    scId,
    scShipType,
    effectiveUserDisplayType,
  };
  if (!isAnonymous) {
    userContext.userId = userId;
    userContext.userName = userName;
  }
  return userContext;
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/service/user-details-service.ts